import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { socialMeta, OG_IMAGE as SHARED_OG_IMAGE } from "@/lib/social-meta";
import { useMemo, useRef, useState } from "react";
import { MarketingShell } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Download, ShieldCheck, ClipboardList } from "lucide-react";

const MAGNET = "security-ops-readiness-checklist-2026";
const TITLE = "2026 Security Operations Readiness Checklist";
const DESCRIPTION =
  "Free 80+ point PDF checklist for private security firms, contract guards, campus security, and patrol operators. Score your ops and find the gaps clients notice.";
// Shared branded 1200x630 card (the previous og-lead-magnet.jpg never shipped).
const OG_IMAGE = SHARED_OG_IMAGE;

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "DigitalDocument",
  name: TITLE,
  description: DESCRIPTION,
  fileFormat: "application/pdf",
  isAccessibleForFree: true,
  inLanguage: "en-US",
  publisher: { "@type": "Organization", name: "MissionReady", url: "https://missionready.dev" },
};

export const Route = createFileRoute("/lead-magnet/security-ops-checklist")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Free PDF | MissionReady` },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: `${TITLE} — Free PDF`,
        description: DESCRIPTION,
        url: "https://missionready.dev/lead-magnet/security-ops-checklist",
        image: OG_IMAGE,
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/lead-magnet/security-ops-checklist" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  }),
  component: LeadMagnetPage,
});

function LeadMagnetPage() {
  const navigate = useNavigate();
  const loadedAt = useMemo(() => Date.now(), []);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const websiteRef = useRef<HTMLInputElement>(null); // honeypot
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      const res = await fetch("/api/public/lead-magnet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name,
          company,
          magnet: MAGNET,
          source: typeof window !== "undefined" ? window.location.pathname : "",
          website: websiteRef.current?.value ?? "",
          loadedAt,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; downloadUrl?: string; error?: string };
      if (!res.ok || !data.ok || !data.downloadUrl) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }
      navigate({
        to: "/lead-magnet/thanks",
        search: { file: data.downloadUrl, email },
      });
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Unable to submit. Please try again.");
    }
  }

  return (
    <MarketingShell>
      <section className="border-b border-border/60 bg-gradient-to-b from-background to-muted/30">
        <PageContainer className="grid gap-10 py-12 md:grid-cols-[1.15fr_1fr] md:py-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <ClipboardList className="h-3.5 w-3.5" /> Free PDF · 6 pages
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              2026 Security Operations Readiness Checklist
            </h1>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              80+ practical checkpoints across officer compliance, post orders, scheduling, GPS accountability,
              patrols, incidents, dispatch, client reporting, billing, and ops resilience. Score your operation and
              find the exact gaps clients notice before they cost you the contract.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Built for 1099 guards, small firms, campus security, PIs, and patrol operators",
                "Score your ops on a 0–80 scale with a maturity rubric",
                "Every item is a real gap that shows up in client reviews",
                "Print it, share it with your team, use it in the next QBR",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4" />
              No credit card. Unsubscribe anytime. We never share your email.
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm md:p-8">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Get the checklist</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your email and we&apos;ll send the PDF to your inbox and open the download.
            </p>
            <form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Work email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  maxLength={255}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jordan Reyes"
                    maxLength={120}
                    autoComplete="name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Firm or agency"
                    maxLength={200}
                    autoComplete="organization"
                  />
                </div>
              </div>

              {/* Honeypot — hidden from real users */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Website
                  <input ref={websiteRef} type="text" tabIndex={-1} autoComplete="off" />
                </label>
              </div>

              {error ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  {error}
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={status === "submitting"}>
                {status === "submitting" ? "Sending…" : "Email me the checklist"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                By downloading you agree to receive the checklist and occasional MissionReady updates. Unsubscribe
                anytime.
              </p>
            </form>
          </div>
        </PageContainer>
      </section>

      <section className="py-14">
        <PageContainer>
          <h2 className="text-2xl font-bold">What&apos;s inside the checklist</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              ["1. Officer onboarding & compliance", "Contracts, licenses, first aid, background checks, handbook sign-off."],
              ["2. Site setup & post orders", "Written post orders, access notes, hazards, standing patrol routes."],
              ["3. Scheduling & shift coverage", "Advance publishing, notifications, open shifts, overtime warnings."],
              ["4. Time, attendance & payroll", "Geofenced clock-ins, no-show alerts, exportable timesheets."],
              ["5. GPS & officer accountability", "Live location, patrol history, geofence exits, offline queue."],
              ["6. Patrols & checkpoints", "Verified QR scans, missed-checkpoint alerts, tour reports."],
              ["7. Incident reports & evidence", "Templated reports, photos/videos, timestamps, chain of custody."],
              ["8. Dispatch & response", "Broadcasts, acknowledgements, response-time tracking, SOS."],
              ["9. Client reporting & invoicing", "Branded reports, contract billing, hours-per-site, invoice exports."],
              ["10. Ops resilience", "Backups, offline mode, incident review cadence, insurance readiness."],
            ].map(([h, d]) => (
              <div key={h} className="rounded-xl border border-border/60 bg-card p-5">
                <div className="text-sm font-semibold text-foreground">{h}</div>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      <section className="border-t border-border/60 bg-muted/20 py-14">
        <PageContainer className="text-center">
          <h2 className="text-2xl font-bold">Built by the team behind MissionReady</h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            MissionReady is the all-in-one operations platform for private security firms — scheduling, dispatch,
            GPS, incidents, patrols, invoicing, and client reporting in one place.
          </p>
        </PageContainer>
      </section>
    </MarketingShell>
  );
}
